import { useEffect, useState } from "react";
import {
  ApiError,
  api,
  authMe,
  changePassword,
  hasCapability,
  listSessions,
  revokeSession,
  type ConsoleAuthState,
  type ConsoleSessionDto,
} from "../lib/api";
import {
  Dot,
  ErrorState,
  PageHeader,
  SectionHeader,
  SkeletonRows,
  StatusBadge,
  toneForStatus,
} from "../components/ui";
import { formatTimestamp } from "../lib/format";

interface DoctorResponse {
  status: string;
  checks: Record<string, string | number>;
}

interface AuditRow {
  id: string;
  action: string;
  targetType: string;
  targetId: string | null;
  createdAt: string;
}

const TABS = [
  { id: "account", label: "Account" },
  { id: "health", label: "System health" },
  { id: "audit", label: "Audit log" },
] as const;

export default function SettingsPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("account");
  const [doctor, setDoctor] = useState<DoctorResponse | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [auth, setAuth] = useState<ConsoleAuthState | null>(null);
  const [sessions, setSessions] = useState<ConsoleSessionDto[]>([]);
  const [passwords, setPasswords] = useState({ current: "", next: "" });
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      // Identity first: every other section depends on the role's capabilities.
      const me = await authMe();
      setAuth(me);
      const [d, s] = await Promise.all([
        api<DoctorResponse>("/api/doctor").catch(() => null),
        listSessions()
          .then((res) => res.data)
          .catch(() => []),
      ]);
      setDoctor(d);
      setSessions(s);
      // The audit trail needs the "write" capability; a viewer must not get a
      // page-level failure just because one section is out of reach.
      if (hasCapability(me, "write")) {
        const a = await api<{ data: AuditRow[] }>("/api/audit").catch(() => ({ data: [] }));
        setAudit(a.data);
      } else {
        setAudit([]);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load settings data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setPasswordNotice(null);
    setPasswordError(null);
    try {
      const res = await changePassword(passwords.current, passwords.next);
      setPasswords({ current: "", next: "" });
      setPasswordNotice(
        res.otherSessionsRevoked > 0
          ? `Password changed. ${res.otherSessionsRevoked} other session(s) signed out.`
          : "Password changed.",
      );
      await load();
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : "Could not change the password");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(id: string) {
    setBusy(true);
    try {
      await revokeSession(id);
      const stillCurrent = sessions.find((s) => s.id === id)?.current;
      if (stillCurrent) {
        window.location.reload();
        return;
      }
      await load();
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : "Could not revoke the session");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl pb-16">
      <PageHeader
        title="Settings"
        subtitle="System health diagnostics and security audit trail."
        right={
          <div className="flex rounded-lg border border-canvas-border bg-canvas-raised p-0.5">
            {(auth?.user
              ? TABS.filter((t) => t.id !== "audit" || hasCapability(auth, "write"))
              : TABS
            ).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  tab === t.id ? "bg-accent/15 text-accent" : "text-canvas-muted hover:text-canvas-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        }
      />

      {error && <div className="mb-4"><ErrorState message={error} onRetry={load} /></div>}
      {loading && <SkeletonRows rows={5} height={56} />}

      {!loading && tab === "account" && (
        <div className="space-y-6">
          <section className="panel p-4">
            <SectionHeader
              title="Signed in as"
              subtitle="Your account and what your role is allowed to do."
            />
            <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-xs text-canvas-muted">Username</dt>
                <dd className="font-medium">{auth?.user?.username ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-canvas-muted">Role</dt>
                <dd className="font-medium">
                  <StatusBadge label={auth?.user?.role ?? "unknown"} tone="ok" />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-canvas-muted">Capabilities</dt>
                <dd className="flex flex-wrap gap-1 pt-0.5">
                  {(auth?.user?.capabilities ?? []).map((c) => (
                    <span key={c} className="chip-muted">
                      {c}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>
          </section>

          <section className="panel p-4">
            <SectionHeader
              title="Change password"
              subtitle={`At least ${auth?.minPasswordLength ?? 10} characters. Other sessions are signed out.`}
            />
            <form className="mt-4 max-w-md space-y-4" onSubmit={submitPassword}>
              <div>
                <label className="label">Current password</label>
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                />
              </div>
              <div>
                <label className="label">New password</label>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={passwords.next}
                  onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                />
              </div>
              {passwordError && (
                <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
                  {passwordError}
                </div>
              )}
              {passwordNotice && (
                <div className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-accent">
                  {passwordNotice}
                </div>
              )}
              <button
                className="btn-primary"
                type="submit"
                disabled={busy || !passwords.current || !passwords.next}
              >
                {busy ? "Saving…" : "Change password"}
              </button>
            </form>
          </section>

          <section className="panel overflow-x-auto">
            <div className="panel-header">
              <h2 className="text-sm font-semibold">Active sessions</h2>
              <span className="chip-muted">{sessions.length} live</span>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-canvas-border">
                  <th className="table-th">Session</th>
                  <th className="table-th">Started</th>
                  <th className="table-th">Expires</th>
                  <th className="table-th">Client</th>
                  <th className="table-th" />
                </tr>
              </thead>
              <tbody className="divide-y divide-canvas-border">
                {sessions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="table-td text-canvas-muted">
                      No active sessions.
                    </td>
                  </tr>
                )}
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td className="table-td">
                      {s.current ? <StatusBadge label="this device" tone="ok" /> : <span className="mono text-xs text-canvas-muted">{s.id}</span>}
                    </td>
                    <td className="table-td mono text-xs text-canvas-muted">
                      {formatTimestamp(s.createdAt)}
                    </td>
                    <td className="table-td mono text-xs text-canvas-muted">
                      {formatTimestamp(s.expiresAt)}
                    </td>
                    <td className="table-td text-xs text-canvas-muted">
                      {s.ip ?? "—"}
                      <div className="truncate" style={{ maxWidth: 260 }}>
                        {s.userAgent ?? ""}
                      </div>
                    </td>
                    <td className="table-td">
                      <button
                        className="btn-ghost !px-2 !py-1 !text-xs"
                        disabled={busy}
                        onClick={() => void handleRevoke(s.id)}
                      >
                        {s.current ? "Sign out" : "Revoke"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}

      {!loading && tab === "health" && (
        <section className="panel">
          <div className="panel-header">
            <h2 className="text-sm font-semibold">System health</h2>
            {doctor && (
              <StatusBadge
                label={doctor.status === "ok" ? "All checks passing" : doctor.status}
                tone={doctor.status === "ok" ? "ok" : "warn"}
              />
            )}
          </div>
          {!doctor ? (
            <p className="px-4 py-6 text-sm text-canvas-muted">
              Doctor endpoint unreachable — is the server running?
            </p>
          ) : (
            <ul className="divide-y divide-canvas-border">
              {Object.entries(doctor.checks).map(([k, v]) => {
                const s = String(v);
                const healthy =
                  !/fail|error|missing|invalid/i.test(s) &&
                  !/fail|error|missing|invalid/i.test(k);
                return (
                  <li key={k} className="flex items-center justify-between px-4 py-2.5">
                    <span className="flex items-center gap-2.5 text-sm">
                      <Dot tone={healthy ? "ok" : "danger"} />
                      {k}
                    </span>
                    <span className="mono text-xs text-canvas-muted">{s}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {!loading && tab === "audit" && (
        <section className="panel overflow-x-auto">
          <div className="panel-header">
            <h2 className="text-sm font-semibold">Audit log</h2>
            <span className="chip-muted">{audit.length} events</span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-canvas-border">
                <th className="table-th">Action</th>
                <th className="table-th">Target</th>
                <th className="table-th">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-canvas-border">
              {audit.length === 0 && (
                <tr>
                  <td colSpan={3} className="table-td text-canvas-muted">
                    No audit events yet — credential and key operations appear here.
                  </td>
                </tr>
              )}
              {audit.map((a) => (
                <tr key={a.id}>
                  <td className="table-td">
                    <StatusBadge
                      label={a.action}
                      tone={
                        /revoke|delete|disable/.test(a.action)
                          ? "warn"
                          : toneForStatus("muted")
                      }
                    />
                  </td>
                  <td className="table-td mono text-xs text-canvas-muted">
                    {a.targetType}
                    {a.targetId ? ` · ${a.targetId}` : ""}
                  </td>
                  <td className="table-td mono text-xs text-canvas-muted">
                    {formatTimestamp(a.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
